import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import styles from "./Carousel.module.scss";
import CarouselProps from "./CarouselProps.interface";

import getInViewChildren from "./helpers/getInViewChildren";

import useDebounce from "hooks/useDebounce";
import IconViewer from "components/IconViewer/IconViewer";
import combineClasses from "helpers/combine-classes";

const Carousel: FunctionComponent<CarouselProps> = (props) => {
    const listElementRef = useRef<HTMLDivElement>(null);
    const inputElementRef = useRef<HTMLInputElement>(null);

    const [showScrollBar, setShowScrollbar] = useState<boolean>(false);

    const [scrollbarPosition, setScrollbarPosition] = useState<any>({});
    const debouncedScrollbarPosition = useDebounce(scrollbarPosition, 25);

    // Show scrollbar
    useEffect(() => {
        setTimeout(() => {
            const showScrollBar = listElementRef?.current && (listElementRef.current.scrollWidth - listElementRef.current.clientWidth) > 0;
            setShowScrollbar(showScrollBar ?? false);
        }, 100);
    }, [listElementRef.current?.scrollWidth, listElementRef.current?.clientWidth]);

    // Scrollbar position (master)
    const onScrollHandler = (alias: string, event: any) => {
        if (listElementRef.current) {
            let result = 0;

            // Finding scroll position
            if (alias === "LIST") {
                const maxScrollWidth = listElementRef.current.scrollWidth - listElementRef.current.clientWidth;
                result = 100 * listElementRef.current.scrollLeft / maxScrollWidth;
            }
            else if (alias === "INPUT") {
                const maxScrollWidth = listElementRef.current.scrollWidth - listElementRef.current.clientWidth;
                result = (maxScrollWidth / 100) * event.target.value;
            }

            setScrollbarPosition({
                alias: alias,
                scroll: result
            });
        }
    };

    const onPreviousClick = () => {
        const inViewIndexes = getInViewChildren(listElementRef.current);

        if (inViewIndexes && inViewIndexes?.length > 0) {
            const firstIndex = inViewIndexes[0];

            if (listElementRef.current && typeof (firstIndex) !== "undefined" && firstIndex < 2) {
                listElementRef.current.scrollLeft = 0;
            }
            else if (listElementRef.current && firstIndex) {
                listElementRef.current.children[firstIndex - 1]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
            }
        }
    };

    const onNextClick = () => {
        const inViewIndexes = getInViewChildren(listElementRef.current);

        if (inViewIndexes && inViewIndexes?.length > 0) {
            const lastIndex = inViewIndexes[inViewIndexes.length - 1];

            if (listElementRef.current && lastIndex === listElementRef.current.children.length - 1) {
                listElementRef.current.scrollLeft = listElementRef.current.scrollWidth;
            }
            else if (listElementRef.current) {
                listElementRef.current.children[lastIndex + 1]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "end" });
            }
        }
    };

    // Scrollbar position (slave)
    useEffect(() => {
        if (typeof (debouncedScrollbarPosition.alias) !== "undefined") {
            // Outputting to others
            if (debouncedScrollbarPosition.alias !== "INPUT" && inputElementRef?.current) {
                inputElementRef.current.value = debouncedScrollbarPosition.scroll;
            }
            if (debouncedScrollbarPosition.alias !== "LIST" && listElementRef?.current) {
                listElementRef.current.scrollLeft = debouncedScrollbarPosition.scroll;
            }
        }
    }, [debouncedScrollbarPosition]);

    return (
        <div className={styles.wrapper}>
            {/* List */}
            <div className={styles.list} ref={listElementRef} onScroll={(event) => onScrollHandler("LIST", event)}>
                {props.children}
            </div>

            {/* Pagination */}
            {showScrollBar === true ? (
                <div className={styles.pagination}>
                    <div className={styles.pagination_inner}>
                        <button className={combineClasses([styles.previous])} onClick={() => onPreviousClick()}>
                            <IconViewer name="arrow-left" />
                        </button>
                        <button className={combineClasses([styles.next])} onClick={() => onNextClick()}>
                            <IconViewer name="arrow-right" />
                        </button>

                        {/* Scrollbar */}
                        <div className={styles.scroll_wrapper}>
                            <input className={styles.scroll_input} min="0" max="100" step="1" type="range" defaultValue="0" ref={inputElementRef} onChange={(event) => onScrollHandler("INPUT", event)} />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default Carousel;